import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
import { SERVICE_ATC_CARTS, MISSING_VALUES } from '@nike/cart-constants';
import { DOTCOM_UX_ID, NIKECOM_CHANNEL, NIKE_BRAND } from '@nike/ux-tread-dotcom-constants';
import { onInitCart } from './action-creators';
import updateCart from './update-cart';

/**
 * AddToCart adds items to your cart (currently via buy/carts/v2).
 *
 * @param {object} params - named parameters
 * @param {Array} params.designViewIds - Unique identifiers for any customization items.
 * @param {Array} params.items - Array of items: each has prop of skuId and quantity.
 * @param {string} params.amount - gift card amount
 * @param {string} [params.appId=DOTCOM_UX_ID] - App specific id used with unite config.
 * @param {string} [params.brand=NIKE_BRAND] - brand is a differentiation for carts, by default it's set to NIKE
 * @param {string} [params.channel=NIKECOM_CHANNEL] - channel is a differentiation for carts, by default it's set to NIKECOM
 * @param {string} params.cloudStack - cloud_stack header to pass along to ux-tread-cart
 * @param {string} params.country - country code
 * @param {string} params.itemId - itemId is an optional param for editing an existing item in cart
 * @param {string} params.langLocale - language locale
 * @param {string} params.language - cloud format - ux-tread-localization/getLanguage
 * @param {string} params.localeForCurrency - BCP 47 language tag - ux-tread-localization/getLocaleForCurrencyFormatting
 * @param {string} params.merchGroup - locale specific information {us|ap|eu}
 * @param {string} params.postalAddress1 - gift card recipient's address line 1
 * @param {string} params.postalAddress2 - gift card recipient's address line 2
 * @param {string} params.postalCity - gift card recipient's city
 * @param {string} params.postalCountry - gift card recipient's country
 * @param {string} params.postalState - gift card recipient's state
 * @param {string} params.postalZipCode - gift card recipient's zip code
 * @param {string} params.quantity - gift card quantity
 * @param {string} params.recipientEmailAddress - gift card recipient's email
 * @param {string} params.recipientFirstName - gift card recipient's first name
 * @param {string} params.recipientLastName - gift card recipient's last name
 * @param {string} params.shippingMethod - shipping method for a gift card
 * @param {string} params.token - unite authToken
 * @param {string} params.visitId - visit id
 * @param {string} params.visitorId - visitor id
 * @param {string} params.host - host name to use when calling cart service
 *
 * @returns {Promise} thenable return with dispatched action results
 */
export default function addToCart(_ref) {
  var _ref$appId = _ref.appId,
    appId = _ref$appId === void 0 ? DOTCOM_UX_ID : _ref$appId,
    host = _ref.host,
    country = _ref.country,
    merchGroup = _ref.merchGroup,
    designViewIds = _ref.designViewIds,
    _ref$brand = _ref.brand,
    brand = _ref$brand === void 0 ? NIKE_BRAND : _ref$brand,
    _ref$channel = _ref.channel,
    channel = _ref$channel === void 0 ? NIKECOM_CHANNEL : _ref$channel,
    cloudStack = _ref.cloudStack,
    itemId = _ref.itemId,
    items = _ref.items,
    token = _ref.token,
    visitId = _ref.visitId,
    visitorId = _ref.visitorId,
    amount = _ref.amount,
    postalAddress1 = _ref.postalAddress1,
    postalAddress2 = _ref.postalAddress2,
    postalCity = _ref.postalCity,
    postalCountry = _ref.postalCountry,
    postalState = _ref.postalState,
    postalZipCode = _ref.postalZipCode,
    quantity = _ref.quantity,
    recipientEmailAddress = _ref.recipientEmailAddress,
    recipientFirstName = _ref.recipientFirstName,
    recipientLastName = _ref.recipientLastName,
    shippingMethod = _ref.shippingMethod;
  return function (dispatch) {
    if (!(visitorId || token) || !country) {
      return Promise.resolve().then(function () {
        return dispatch({
          type: MISSING_VALUES,
          payload: {
            visitId: visitId,
            visitorId: visitorId,
            token: token,
            country: country
          }
        });
      });
    }
    var params = {
      host: host,
      token: token,
      visitId: visitId,
      visitorId: visitorId,
      brand: brand,
      channel: channel,
      designViewIds: designViewIds,
      appId: appId,
      country: country,
      items: items,
      cloudStack: cloudStack,
      id: itemId
    };

    // Gift Card specific props
    if (amount) {
      var item = params.items && params.items[0];
      item.giftCard = {
        amount: amount
      };
      item.shippingMethod = shippingMethod;
      item.shippingAddress = {
        country: country
      };
      item.recipient = {
        firstName: recipientFirstName,
        lastName: recipientLastName
      };
      if (recipientEmailAddress) {
        item.shippingAddress.email = recipientEmailAddress;
        item.quantity = 1;
      } else {
        // Physical Gift Cards
        item.shippingAddress = _objectSpread(_objectSpread({}, item.shippingAddress), {}, {
          address1: postalAddress1,
          address2: postalAddress2,
          city: postalCity,
          state: postalState,
          postalCode: postalZipCode,
          country: postalCountry || country
        });
        item.quantity = parseInt(quantity, 10) || 1;
      }
    }
    dispatch(onInitCart(_objectSpread({
      service: SERVICE_ATC_CARTS,
      country: country,
      merchGroup: merchGroup,
      appId: appId
    }, params)));
    return dispatch(updateCart(params));
  };
}